import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  TabsContainer,
  TabsBar,
  TabItem,
  TabText,
  ViewModeToggle,
} from "./TabsComponent.styles";
import { useLocation, useNavigate } from "react-router-dom";
import {
  resetCollectionTabAnimation,
  selectCollectionTabAnimating,
} from "../../../store/slices/common/slice";
import { ReactComponent as FeedIcon } from "../../../assets/icons/feedIcon.svg";
import { ReactComponent as GridIcon } from "../../../assets/icons/exploreLayout.svg";
import { HiOutlineSparkles, HiSparkles, HiUser } from "react-icons/hi2";
import { FiUser } from "react-icons/fi";

const TABS = [
  { name: "Explore", path: "/", Icon: HiOutlineSparkles, ActiveIcon: HiSparkles },
  { name: "Mine", path: "/mine", Icon: FiUser, ActiveIcon: HiUser },
];

const SLIDE_DURATION = 280;

const TabsComponent = ({
  isFeedView = false,
  onToggleViewMode,
  collectionPage,
  showViewModeToggle = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isCollectionAnimating = useSelector(selectCollectionTabAnimating);
  const navigatingRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(() => {
    const idx = TABS.findIndex((t) => t.path === location.pathname);
    return idx === -1 ? 0 : idx;
  });

  // Sync activeIndex when location changes externally (e.g. back/forward)
  useEffect(() => {
    if (!navigatingRef.current) {
      const idx = TABS.findIndex((t) => t.path === location.pathname);
      setActiveIndex(idx === -1 ? 0 : idx);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isCollectionAnimating) {
      const timer = setTimeout(() => {
        dispatch(resetCollectionTabAnimation());
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isCollectionAnimating, dispatch]);

  const handleTabClick = useCallback(
    (tab, index) => {
      // Already on this tab — do nothing (no re-render, no scroll, no navigate)
      if (index === activeIndex) return;

      // Set index immediately so the slide animation plays
      setActiveIndex(index);
      navigatingRef.current = true;

      // Navigate after the slide animation completes
      setTimeout(() => {
        navigate(tab.path);
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        navigatingRef.current = false;
      }, SLIDE_DURATION);
    },
    [activeIndex, navigate],
  );

  return (
    <TabsContainer $collectionPage={collectionPage}>
      {/* $scrolled is hardcoded true now — the previous scroll-driven
          flip between transparent and frosted-pill is gone. The tabs
          always show the colored, glassy pill background. */}
      <TabsBar $activeIndex={activeIndex} $scrolled={true}>
        {TABS.map((tab, index) => {
          const isActive = index === activeIndex;
          const Icon = isActive ? tab.ActiveIcon : tab.Icon;
          return (
            <TabItem
              key={tab.path}
              type="button"
              $active={isActive}
              onClick={() => handleTabClick(tab, index)}
            >
              {Icon && <Icon aria-hidden="true" />}
              <TabText
                $isAnimating={tab.path === "/mine" && isCollectionAnimating}
              >
                {tab.name}
              </TabText>
            </TabItem>
          );
        })}
      </TabsBar>
      {showViewModeToggle && onToggleViewMode && (
        <ViewModeToggle
          type="button"
          $active={isFeedView}
          onClick={onToggleViewMode}
          aria-label={
            isFeedView ? "Switch to grid explore" : "Switch to feed explore"
          }
          title={isFeedView ? "Grid explore" : "Feed explore"}
        >
          {isFeedView ? <GridIcon /> : <FeedIcon />}
        </ViewModeToggle>
      )}
    </TabsContainer>
  );
};

export default TabsComponent;
